// src/utils/validation.js - Input validation utilities for form security
export class ValidationError extends Error {
  constructor(field, message) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

// Common validation patterns
const PATTERNS = {
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  phone: /^[+]?[1-9][\d\s\-()]{7,15}$/,
  name: /^[a-zA-Z\s]{2,50}$/,
  alphanumeric: /^[a-zA-Z0-9\s]{1,}$/,
  numeric: /^\d+(\.\d{1,2})?$/,
  url: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
};

// Sanitization functions
export const sanitize = {
  string: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .trim()
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .slice(0, 500); // Limit length
  },
  
  email: (input) => {
    return sanitize.string(input).toLowerCase();
  },
  
  phone: (input) => {
    return sanitize.string(input).replace(/[^\d+\-\s()]/g, '');
  },
  
  numeric: (input) => {
    const num = parseFloat(input);
    return isNaN(num) ? 0 : Math.max(0, num);
  },
  
  name: (input) => {
    return sanitize.string(input)
      .replace(/[^a-zA-Z\s]/g, '')
      .replace(/\s+/g, ' '); // Normalize spaces
  }
};

// Validation functions
export const validate = {
  required: (value, fieldName = 'Field') => {
    const sanitized = sanitize.string(value);
    if (!sanitized || sanitized.length === 0) {
      throw new ValidationError(fieldName, `${fieldName} is required`);
    }
    return sanitized;
  },

  email: (value, fieldName = 'Email') => {
    const sanitized = sanitize.email(value);
    validate.required(sanitized, fieldName);
    
    if (!PATTERNS.email.test(sanitized)) {
      throw new ValidationError(fieldName, 'Please enter a valid email address');
    }
    
    if (sanitized.length > 254) {
      throw new ValidationError(fieldName, 'Email address is too long');
    }
    
    return sanitized;
  },

  phone: (value, fieldName = 'Phone') => {
    const sanitized = sanitize.phone(value);
    validate.required(sanitized, fieldName);
    
    if (!PATTERNS.phone.test(sanitized)) {
      throw new ValidationError(fieldName, 'Please enter a valid phone number');
    }
    
    return sanitized;
  },

  name: (value, fieldName = 'Name') => {
    const sanitized = sanitize.name(value);
    validate.required(sanitized, fieldName);
    
    if (!PATTERNS.name.test(sanitized)) {
      throw new ValidationError(fieldName, 'Name can only contain letters and spaces');
    }
    
    if (sanitized.length < 2) {
      throw new ValidationError(fieldName, 'Name must be at least 2 characters long');
    }
    
    if (sanitized.length > 50) {
      throw new ValidationError(fieldName, 'Name must be less than 50 characters');
    }
    
    return sanitized;
  },

  address: (value, fieldName = 'Address') => {
    const sanitized = sanitize.string(value);
    validate.required(sanitized, fieldName);
    
    if (sanitized.length < 10) {
      throw new ValidationError(fieldName, 'Please enter a complete address');
    }
    
    if (sanitized.length > 200) {
      throw new ValidationError(fieldName, 'Address is too long');
    }
    
    return sanitized;
  },

  pincode: (value, fieldName = 'Pincode') => {
    const sanitized = sanitize.string(value).replace(/\D/g, '');
    
    if (sanitized.length !== 6) {
      throw new ValidationError(fieldName, 'Pincode must be 6 digits');
    }
    
    return sanitized;
  },

  price: (value, fieldName = 'Price') => {
    const sanitized = sanitize.numeric(value);
    
    if (sanitized < 0) {
      throw new ValidationError(fieldName, 'Price cannot be negative');
    }
    
    if (sanitized > 1000000) {
      throw new ValidationError(fieldName, 'Price is too high');
    }
    
    return sanitized;
  },

  quantity: (value, fieldName = 'Quantity') => {
    const sanitized = Math.floor(sanitize.numeric(value));
    
    if (sanitized < 1) {
      throw new ValidationError(fieldName, 'Quantity must be at least 1');
    }
    
    if (sanitized > 100) {
      throw new ValidationError(fieldName, 'Quantity cannot exceed 100');
    }
    
    return sanitized;
  },

  url: (value, fieldName = 'URL') => {
    const sanitized = sanitize.string(value);
    
    if (sanitized && !PATTERNS.url.test(sanitized)) {
      throw new ValidationError(fieldName, 'Please enter a valid URL');
    }
    
    return sanitized;
  },

  referralCode: (value, fieldName = 'Referral Code') => {
    if (!value) return ''; // Optional field
    
    const sanitized = sanitize.string(value).toUpperCase();
    
    if (sanitized && (sanitized.length < 3 || sanitized.length > 20)) {
      throw new ValidationError(fieldName, 'Referral code must be between 3-20 characters');
    }
    
    if (sanitized && !/^[A-Z0-9]+$/.test(sanitized)) {
      throw new ValidationError(fieldName, 'Referral code can only contain letters and numbers');
    }
    
    return sanitized;
  }
};

// Form validation helper
export const validateForm = (formData, validationSchema) => {
  const errors = {};
  const sanitizedData = {};
  
  for (const [field, validator] of Object.entries(validationSchema)) {
    try {
      sanitizedData[field] = validator(formData[field]);
    } catch (error) {
      if (error instanceof ValidationError) {
        errors[error.field || field] = error.message;
      } else {
        errors[field] = 'Invalid input';
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedData
  };
};

// Signup form validation schema
export const signupValidationSchema = {
  fullName: (value) => validate.name(value, 'Full Name'),
  email: (value) => validate.email(value, 'Email'),
  phone: (value) => validate.phone(value, 'Phone'),
  address: (value) => validate.address(value, 'Address'),
  pincode: (value) => validate.pincode(value, 'Pincode'),
  referralCode: (value) => validate.referralCode(value, 'Referral Code')
};

// Order form validation schema
export const orderValidationSchema = {
  fullName: (value) => validate.name(value, 'Full Name'),
  email: (value) => validate.email(value, 'Email'),
  phone: (value) => validate.phone(value, 'Phone'),
  address: (value) => validate.address(value, 'Delivery Address'),
  pincode: (value) => validate.pincode(value, 'Pincode')
};

// Product form validation schema (admin)
export const productValidationSchema = {
  name: (value) => validate.required(sanitize.string(value), 'Product Name'),
  description: (value) => validate.required(sanitize.string(value), 'Description'),
  price: (value) => validate.price(value, 'Price'),
  category: (value) => validate.required(sanitize.string(value), 'Category'),
  imageUrl: (value) => validate.url(value, 'Image URL')
};

// User update validation schema (admin)
export const userUpdateValidationSchema = {
  fullName: (value) => validate.name(value, 'Full Name'),
  email: (value) => validate.email(value, 'Email'),
  phone: (value) => validate.phone(value, 'Phone'),
  cashbackBalance: (value) => validate.price(value, 'Cashback Balance'),
  cashbackEarned: (value) => validate.price(value, 'Cashback Earned'),
  cashbackUsed: (value) => validate.price(value, 'Cashback Used'),
  commissionBalance: (value) => validate.price(value, 'Points Balance'),
  commissionEarned: (value) => validate.price(value, 'Points Earned'),
  commissionUsed: (value) => validate.price(value, 'Points Used'),
  totalReferrals: (value) => validate.quantity(value, 'Total Referrals')
};

// Delivery fee calculation utility
export const calculateDeliveryFee = (totalAmount, userEmail = null) => {
  // Admin gets free delivery
  if (userEmail === "infinitywhale08@gmail.com") {
    return 0; // Free shipping for admin
  }
  
  if (totalAmount >= 2999) {
    return 0; // Free shipping
  } else if (totalAmount >= 999) {
    return 250; // ₹999-₹2998 range
  } else {
    return 150; // Below ₹999
  }
};

// Get delivery fee message for display
export const getDeliveryMessage = (totalAmount, userEmail = null) => {
  const fee = calculateDeliveryFee(totalAmount, userEmail);
  
  if (fee === 0) {
    return {
      isFree: true,
      message: "🎉 Free shipping on your order!",
      additionalMessage: null
    };
  }
  
  const amountNeeded = 2999 - totalAmount;
  return {
    isFree: false,
    fee: fee,
    message: `🚚 Delivery fee: ₹${fee}`,
    additionalMessage: `Add ₹${amountNeeded} more to get free shipping`
  };
};